import type { AgentConfig } from '@kanbu/schema';
import { Role } from '@kanbu/schema/enums';
import { useLingui } from '@lingui/react/macro';
import type { TypedFormState } from '@utima/ui-informed';
import { memo } from 'react';

import { Card } from '@/components/card/Card';
import { SchemaFields } from '@/components/form/SchemaFields';
import { useFieldsSchema } from '@/components/form/useFieldsSchema';
import { useAccess } from '@/hooks/useAccess';

export type OverridesFieldsProps = {};

export const OverridesFields = memo(
  function OverridesFields({}: OverridesFieldsProps) {
    const { t } = useLingui();
    const { hasAccess } = useAccess();

    const { schema } = useFieldsSchema(
      [
        {
          name: 'overrides',
          label: 'Enable overrides',
          type: 'checkbox',
          uiControl: 'switch',
          defaultValue: false,
          visible: hasAccess([Role.SuperAdmin]),
        },
        {
          $relevant: {
            $when: ({ formState }) =>
              !!(formState as TypedFormState<{ overrides: boolean }>).values
                ?.overrides,
            $fields: [
              {
                name: 'agentConfig.historyTokenBudget',
                label: t`Conversation history token budget`,
                type: 'number',
                visible: hasAccess([Role.SuperAdmin]),
                tooltip: t`Tokens of recent user and assistant dialogue to retain before compacting the oldest turns. Default: 48,000. Higher values preserve more context and increase cost.`,
              },
              {
                name: 'agentConfig.toolResultHistoryTokenBudget',
                label: t`Historical tool results token budget`,
                type: 'number',
                visible: hasAccess([Role.SuperAdmin]),
                tooltip: t`Separate allowance for earlier tool results. Default: 24,000. Recent results are kept first; original messages remain available through history search.`,
              },
              {
                name: 'agentConfig.hybridAlpha',
                visible: hasAccess([Role.SuperAdmin]),
                label: 'hybridAlpha',
                tooltip:
                  'An alpha of 1 is a pure vector search. An alpha of 0 is a pure keyword search.',
                type: 'number',
                defaultValue: 0.7,
                uiProps: { min: 0, max: 1, step: 0.1 },
              },
              {
                name: 'agentConfig.retrievalCount',
                visible: hasAccess([Role.SuperAdmin]),
                label: 'retrievalCount',
                type: 'number',
                defaultValue: 50,
                uiProps: { min: 1, max: 250, step: 1 },
              },
              {
                name: 'agentConfig.retrievalTokenCap',
                visible: hasAccess([Role.SuperAdmin]),
                label: 'retrievalTokenCap',
                tooltip:
                  'Max tokens of a single documentSearch result fed back to the model. Higher = the model sees more chunks in the live turn (better recall) but each search costs more replayed context on later turns. Leave empty for the runtime default (12000).',
                type: 'number',
                uiProps: {
                  min: 500,
                  max: 16_000,
                  step: 500,
                },
              },
              {
                name: 'agentConfig.rerank',
                visible: hasAccess([Role.SuperAdmin]),
                label: 'rerank',
                type: 'checkbox',
                uiControl: 'switch',
                defaultValue: true,
              },
              {
                $relevant: {
                  $when: ({ formState }) =>
                    !!(
                      formState as TypedFormState<{ agentConfig: AgentConfig }>
                    ).values?.agentConfig?.rerank,
                  $fields: [
                    {
                      name: 'agentConfig.rerankCount',
                      visible: hasAccess([Role.SuperAdmin]),
                      label: 'rerankCount',
                      type: 'number',
                      defaultValue: 10,
                      uiProps: { min: 1, max: 100, step: 1 },
                    },
                    {
                      name: 'agentConfig.rerankCoefficient',
                      visible: hasAccess([Role.SuperAdmin]),
                      label: 'rerankCoefficient',
                      type: 'number',
                      defaultValue: 0.7,
                      tooltip:
                        'The coefficient that determines the influence of the reranked documents on the final result.',
                      uiProps: {
                        min: 0,
                        max: 1,
                        step: 0.1,
                      },
                    },
                  ],
                },
              },
              {
                name: 'agentConfig.weightRerankCoefficient',
                visible: hasAccess([Role.SuperAdmin]),
                label: 'weightRerankCoefficient',
                type: 'number',
                defaultValue: 0.3,
                tooltip:
                  'Final ranking influence of document priority. At 0.3, relevance contributes 70% and the configured document weight contributes 30%. Applied before the top-result cut, including when reranking is off or unavailable.',
                uiProps: {
                  min: 0,
                  max: 1,
                  step: 0.1,
                },
              },
              {
                name: 'agentConfig.minScore',
                visible: hasAccess([Role.SuperAdmin]),
                label: 'minScore',
                tooltip:
                  'Absolute relevance floor before document-priority boosting (0-1); 0 or empty disables it. Use cautiously: when the reranker is unavailable, fallback hybrid scores use a different scale, so the same floor can remove a different share of results.',
                type: 'number',
                uiProps: {
                  min: 0,
                  max: 1,
                  step: 0.05,
                },
              },
            ],
          },
        },
      ],
      [t, hasAccess],
    );

    return (
      <Card title='Overrides' variant='secondary'>
        <SchemaFields schema={schema} />
      </Card>
    );
  },
);
